<template>
  <div v-if="!removed" class="comment-thread" data-testid="comment-thread">
    <CommentTombstone v-if="root.deleted_at" />
    <CommentItem
      v-else
      :comment="root"
      :viewer="viewer"
      :resolved="Boolean(root.resolved)"
      :one-to-one="oneToOne"
      @reply="replying = true"
      @resolve="setResolved(true)"
      @updated="onRootUpdated"
      @deleted="onRootDeleted"
    />

    <ResolvedThreadBar
      v-if="root.resolved"
      class="comment-thread__bar"
      :reply-count="liveReplyList.length"
      :resolved-on="resolvedOn"
      :expanded="expanded"
      @toggle="expanded = !expanded"
      @reopen="setResolved(false)"
    />

    <div v-if="showReplies && liveReplyList.length" class="comment-thread__replies">
      <CommentItem
        v-for="reply in liveReplyList"
        :key="reply.id"
        :comment="reply"
        :viewer="viewer"
        is-reply
        :one-to-one="oneToOne"
        @reply="replying = true"
        @updated="onReplyUpdated"
        @deleted="onReplyDeleted"
      />
    </div>

    <ChatterComposer
      v-if="replying"
      class="comment-thread__composer"
      inline
      aria-label="Write a reply"
      placeholder="Write a reply…"
      :clear-on-post="false"
      :submitting="posting"
      :value="replyDraft"
      @input="replyDraft = $event"
      @post="postReply"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import ChatterComposer from "@components/ChatterComposer/ChatterComposer.vue";
import CommentItem from "@components/CommentItem/CommentItem.vue";
import CommentTombstone from "@components/CommentTombstone/CommentTombstone.vue";
import ResolvedThreadBar from "@components/ResolvedThreadBar/ResolvedThreadBar.vue";
import {
  formatCommentDate,
  liveReplies,
  visibleCommentIds,
} from "@components/ChatterDrawer/composables/commentUtils";
import { useCommentActions } from "@components/ChatterDrawer/composables/useCommentActions";

const props = defineProps({
  /** A feed entry of kind "comment": { comment, replies }. */
  item: { type: Object, required: true },
  viewer: { type: Object, default: () => ({ accessId: null, isAdmin: false }) },
  objectType: { type: String, default: "" },
  objectId: { type: [String, Number], default: "" },
  oneToOne: { type: Boolean, default: false },
});

const emit = defineEmits(["changed", "visible-change"]);

const actions = useCommentActions();

const root = ref({});
const replies = ref([]);
const removed = ref(false);
const expanded = ref(false);
const replying = ref(false);
const replyDraft = ref("");
const posting = ref(false);

watch(
  () => props.item,
  (item) => {
    root.value = item.comment;
    replies.value = item.replies || [];
    removed.value = false;
  },
  { immediate: true }
);

const liveReplyList = computed(() => liveReplies(replies.value));
const showReplies = computed(() => !root.value.resolved || expanded.value);
const resolvedOn = computed(() => formatCommentDate(root.value.updated_at));

watch(
  [root, replies, showReplies, removed],
  () => {
    const ids = removed.value
      ? []
      : visibleCommentIds(root.value, replies.value, { showReplies: showReplies.value });
    emit("visible-change", { threadId: props.item.comment.id, ids });
  },
  { immediate: true, deep: true }
);

const replaceIn = (list, comment) => list.map((c) => (c.id === comment.id ? comment : c));

function onRootUpdated(comment) {
  root.value = comment;
  emit("changed");
}

function onReplyUpdated(comment) {
  replies.value = replaceIn(replies.value, comment);
  emit("changed");
}

// A deleted root with replies stays as a tombstone; one without replies disappears.
function onRootDeleted() {
  if (liveReplyList.value.length) {
    root.value = { ...root.value, deleted_at: new Date().toISOString(), author: null };
  } else {
    removed.value = true;
  }
  emit("changed");
}

function onReplyDeleted(commentId) {
  replies.value = replies.value.filter((c) => c.id !== commentId);
  emit("changed");
}

async function setResolved(resolved) {
  const result = await actions.setResolved(root.value.id, resolved);
  if (!result.ok) return;
  root.value = result.comment;
  expanded.value = false;
  emit("changed");
}

async function postReply(text) {
  posting.value = true;
  const result = await actions.create({
    objectType: props.objectType,
    objectId: props.objectId,
    body: text,
    parentId: root.value.id,
  });
  posting.value = false;
  if (!result.ok) return;
  replies.value = [...replies.value.filter((c) => c.id !== result.comment.id), result.comment];
  replyDraft.value = "";
  replying.value = false;
  emit("changed");
}
</script>

<style scoped>
.comment-thread {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.comment-thread__replies {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comment-thread__composer {
  padding-left: 44px;
  box-sizing: border-box;
}
</style>
